/**
 * RelayBudgetsPage
 * Per-case wake counts + per-landlord monthly budget; override action for exhausted cases.
 * Route: /settings/agent-email/budgets (a tab of SettingsAgentEmailPage)
 * US-6, US-7
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchCaseBudgets,
  fetchLandlordBudget,
  overrideCaseBudget,
  type CaseBudgetRow,
  type LandlordBudgetRow,
} from '../lib/relay.api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { BudgetStatusBadge } from '../components/relay/BudgetStatusBadge';

// ---------------------------------------------------------------------------
// Override modal
// ---------------------------------------------------------------------------

interface OverrideModalProps {
  caseId: string;
  onConfirm: (additionalWakes: number, note: string) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}

function OverrideModal({ caseId, onConfirm, onClose, isSubmitting }: OverrideModalProps) {
  const [additionalWakes, setAdditionalWakes] = useState(10);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (additionalWakes < 1) {
      setError('Additional wakes must be at least 1');
      return;
    }
    if (!note.trim()) {
      setError('Note is required');
      return;
    }
    setError(null);
    try {
      await onConfirm(additionalWakes, note.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply override');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="override-modal-title"
        className="bg-card rounded-lg border border-border w-[90%] max-w-[480px] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="override-modal-title"
          className="text-lg font-semibold text-foreground mb-1"
        >
          Override Budget
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Case: <span className="font-mono text-xs">{caseId}</span>
        </p>

        <div className="mb-4">
          <label
            htmlFor="additional-wakes"
            className="block text-sm font-medium text-foreground mb-1"
          >
            Additional Wakes
          </label>
          <input
            id="additional-wakes"
            type="number"
            min={1}
            value={additionalWakes}
            onChange={(e) => setAdditionalWakes(parseInt(e.target.value, 10) || 0)}
            className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground outline-none"
            data-testid="override-additional-wakes-input"
            required
            aria-required="true"
            aria-invalid={error && additionalWakes < 1 ? 'true' : undefined}
            aria-describedby={error ? 'override-modal-error' : undefined}
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="override-note"
            className="block text-sm font-medium text-foreground mb-1"
          >
            Note <span className="text-destructive">*</span>
          </label>
          <textarea
            id="override-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Reason for budget override..."
            className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground outline-none resize-y font-[inherit]"
            data-testid="override-note-input"
            required
            aria-required="true"
            aria-invalid={error && !note.trim() ? 'true' : undefined}
            aria-describedby={error ? 'override-modal-error' : undefined}
          />
        </div>

        {error && (
          <p id="override-modal-error" className="text-sm text-destructive mb-3" role="alert">
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !note.trim() || additionalWakes < 1}
            data-testid="override-confirm-button"
          >
            {isSubmitting ? 'Applying...' : 'Apply Override'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Landlord budget card
// ---------------------------------------------------------------------------

/** Thresholds arrive as fractions of the budget (0.75), the page shows whole percents (75). */
function toPercent(fraction: number): number {
  return Math.round(fraction * 100);
}

function LandlordBudgetCard({ budget }: { budget: LandlordBudgetRow }) {
  const used = parseInt(budget.tokenUsed, 10) || 0;
  const total = parseInt(budget.tokenBudget, 10) || 1;
  const pct = Math.min(100, Math.round((used / total) * 100));

  const barColor =
    budget.status === 'exhausted'
      ? 'bg-destructive'
      : budget.status === 'soft_alert'
      ? 'bg-warning'
      : 'bg-success';

  return (
    <div
      data-testid="landlord-budget-card"
      className="rounded-lg border border-border bg-card p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-foreground">Monthly Token Budget</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{budget.monthYear}</p>
        </div>
        <BudgetStatusBadge status={budget.status} />
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div
          className="h-3 rounded-full bg-muted overflow-hidden"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Token usage: ${pct}%`}
        >
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between mt-1 text-xs text-muted-foreground">
          <span>{used.toLocaleString()} used</span>
          <span>{pct}%</span>
          <span>{total.toLocaleString()} total</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-muted-foreground">Soft alert at: </span>
          <span className="text-foreground">{toPercent(budget.softAlertAt)}%</span>
        </div>
        <div>
          <span className="text-muted-foreground">Hard stop at: </span>
          <span className="text-foreground">{toPercent(budget.hardStopAt)}%</span>
        </div>
        <div>
          <span className="text-muted-foreground">Tokens used: </span>
          <span data-testid="landlord-token-used" className="text-foreground">
            {used.toLocaleString()}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Status: </span>
          <span data-testid="landlord-budget-status" className="text-foreground capitalize">
            {budget.status === 'soft_alert' ? 'Soft Alert' : budget.status === 'exhausted' ? 'Exhausted' : 'Within Budget'}
          </span>
        </div>
        {budget.exhaustedAt && (
          <div className="col-span-2">
            <span className="text-muted-foreground">Exhausted at: </span>
            <span className="text-foreground">
              {new Date(budget.exhaustedAt).toLocaleString([], {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString([], {
    month: 'short',
    day: 'numeric',
  });
}

export function RelayBudgetsPage() {
  const [cases, setCases] = useState<CaseBudgetRow[]>([]);
  const [landlordBudget, setLandlordBudget] = useState<LandlordBudgetRow | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrideCaseId, setOverrideCaseId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [casesData, landlordData] = await Promise.all([
        fetchCaseBudgets({ page, sort: 'status' }),
        fetchLandlordBudget(),
      ]);
      setCases(casesData.cases);
      setTotal(casesData.total);
      setLandlordBudget(landlordData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOverrideConfirm = useCallback(
    async (additionalWakes: number, note: string) => {
      if (!overrideCaseId) return;
      setIsSubmitting(true);
      try {
        const result = await overrideCaseBudget(overrideCaseId, { additionalWakes, note });
        setCases((prev) =>
          prev.map((c) => (c.caseId === overrideCaseId ? result.budget : c))
        );
        setOverrideCaseId(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [overrideCaseId]
  );

  const totalPages = Math.ceil(total / 20);

  return (
    <div data-testid="relay-budgets-page">
      {/* Error state */}
      {error && (
        <div
          data-testid="budgets-error"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          role="alert"
        >
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="budgets-retry">
            Retry
          </Button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div data-testid="budgets-loading" className="space-y-4">
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Landlord Monthly Budget */}
          <section className="mb-8">
            <h2 className="text-base font-semibold text-foreground mb-3">
              Landlord Monthly Budget
            </h2>
            {landlordBudget ? (
              <LandlordBudgetCard budget={landlordBudget} />
            ) : (
              <p className="text-sm text-muted-foreground py-4">
                No landlord budget data available.
              </p>
            )}
          </section>

          {/* Per-Case Budgets */}
          <section>
            <h2 className="text-base font-semibold text-foreground mb-3">
              Per-Case Budgets
            </h2>

            {cases.length === 0 ? (
              <div
                data-testid="case-budgets-empty"
                className="py-8 text-center text-muted-foreground"
              >
                <p className="text-sm">No case budget data available.</p>
              </div>
            ) : (
              <div
                data-testid="case-budgets-table"
                className="rounded-lg border border-border overflow-hidden"
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" aria-label="Per-case budget table">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border">
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Case ID
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Kind
                        </th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Wakes
                        </th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Outbound
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Status
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Exhausted
                        </th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {cases.map((c, index) => (
                        <tr
                          key={c.caseId}
                          data-testid={`case-budget-row-${c.caseId}`}
                          className={
                            index < cases.length - 1 ? 'border-b border-border' : ''
                          }
                        >
                          <td className="px-4 py-3 font-mono text-xs text-foreground">
                            {c.caseId.slice(0, 12)}...
                          </td>
                          <td className="px-4 py-3 text-muted-foreground capitalize">
                            {c.caseKind ?? '—'}
                          </td>
                          <td
                            className="px-4 py-3 text-right tabular-nums text-foreground"
                            data-testid="budget-wake-count"
                          >
                            {c.wakeCount} / {c.wakeCap}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-foreground">
                            {c.outboundCount}
                          </td>
                          <td className="px-4 py-3">
                            <BudgetStatusBadge status={c.status} />
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {formatDate(c.exhaustedAt)}
                          </td>
                          <td className="px-4 py-3">
                            {c.status === 'exhausted' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setOverrideCaseId(c.caseId)}
                                data-testid={`budget-override-button-${c.caseId}`}
                              >
                                Override
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                data-testid="case-budgets-pagination"
                className="mt-4 flex items-center justify-between"
              >
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages} ({total} total)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    data-testid="case-budgets-prev-page"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="case-budgets-next-page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {/* Override modal */}
      {overrideCaseId && (
        <OverrideModal
          caseId={overrideCaseId}
          onConfirm={handleOverrideConfirm}
          onClose={() => setOverrideCaseId(null)}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}
